import React, { useEffect, useState } from 'react';
import { useWallet } from "@solana/wallet-adapter-react";
import { Connection, Keypair, PublicKey } from "@solana/web3.js";
import { Coins, Dice5 as Dice, Loader2, RefreshCw, Wallet } from 'lucide-react';
import PayoutTable from './PayoutTable';
import DiceRollAnimation from './DiceRollAnimation';
import { useProgram } from '../hooks/useProgram';
import { derivePlayerStatePda } from '../utils/derive';
import { PayoutConfig, RTD_PROGRAM_ID } from '../utils/instructions';
import * as borsh from "borsh";
import { PlayerState, PlayerStateSchema } from '../PlayerState';
import { createAssociatedTokenAccountInstruction } from '@solana/spl-token';
import { MONOPOLY_MINT } from '../../solana-constants';
import { sendTx } from '../utils/sendTx';
import LoadingIndicator from './Loading';

interface TokenAccountInitProps {
  onInitialize: () => Promise<void>;
  isLoading: boolean;
}

const TokenAccountInit: React.FC<TokenAccountInitProps> = ({ onInitialize, isLoading }) => (
  <div className="flex flex-col items-center justify-center space-y-6 p-8 max-w-2xl mx-auto text-center">
    <div className="text-2xl font-bold text-primary-400">
      Initialize Token Account
    </div>

    <p className="text-gray-300">
      Before you can play, you need to initialize your $MONOPOLY token account. This is a one-time setup that allows you to receive winnings.
    </p>

    <button
      onClick={onInitialize}
      disabled={isLoading}
      className="flex items-center justify-center gap-2 px-8 py-4 bg-primary-600 hover:bg-primary-700 rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
    >
      {isLoading ? (
        <>
          <RefreshCw className="w-5 h-5 animate-spin" />
          <span>Initializing...</span>
        </>
      ) : (
        <>
          <Wallet className="w-10 h-10 lg:w-5 lg:h-5" />
          <span className="text-sm lg:text-base">Initialize $MONOPOLY Account</span>
        </>
      )}
    </button>

    <p className="text-sm text-gray-400">
      You'll need a small amount of SOL to cover the initialization fee.
    </p>
  </div>
);

interface InsufficientRollProps {
  onStakeClick: () => void;
  onBuyClick: () => void;
}

const InsufficientRoll: React.FC<InsufficientRollProps> = ({ onStakeClick, onBuyClick }) => (
  <div className="flex flex-col items-center justify-center space-y-6 mx-auto text-center">
    <div className="text-2xl font-bold text-primary-400">
      Need More $ROLL to Play
    </div>

    <p className="text-gray-300">
      You need at least 1 $ROLL token to play Roll The Dice. You can:
    </p>

    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 w-full max-w-lg">
      <button
        onClick={onStakeClick}
        className="flex items-center justify-center gap-2 px-6 py-4 bg-primary-600 hover:bg-primary-700 rounded-xl transition-colors"
      >
        <Coins className="w-10 h-10 lg:w-5 lg:h-5" />
        <span>Stake $MONOPOLY</span>
      </button>

      <button
        onClick={onBuyClick}
        className=" items-center justify-center gap-2 px-6 py-4 bg-accent-600 hover:bg-accent-700 rounded-xl transition-colors hidden"
      >
        <Wallet className="w-5 h-5" />
        <span>Buy with $SOL</span>
      </button>
    </div>

    <p className="text-sm text-gray-400">
      Stake $MONOPOLY to earn $ROLL tokens over time.
    </p>
  </div>
);

export default function RollTheDiceGame({
  payoutMap = [],
  onInitPlayer,
  onInitRandom,
  onPlay,
  randomnessKp: pRandomnessKp,
  rewardPoolBalance,
  disabled,
  userRollBalance,
  transactionStep,
  userMonopolyTokenAccount,
  isLoadingQueue,
}: {
  payoutMap: PayoutConfig[];
  onInitRandom: () => Promise<Keypair>;
  onInitPlayer: () => Promise<Keypair>;
  onPlay: () => Promise<boolean>;
  randomnessKp: Keypair;
  rewardPoolBalance: string;
  disabled: boolean;
  userRollBalance: string;
  transactionStep: 'play' | 'settle' | null;
  userMonopolyTokenAccount: PublicKey;
  isLoadingQueue: boolean;
}) {
  const { connection } = useProgram();
  const { publicKey, signTransaction } = useWallet();
  const [randomnessKp, setRandomnessKp] = useState(pRandomnessKp);
  const [isInitialized, setIsInitialized] = useState(false);
  const [isRolling, setIsRolling] = useState(false);
  const [hasPlayed, setHasPlayed] = useState(false);
  const [currentRoll, setCurrentRoll] = useState({ dice1: 1, dice2: 1 });
  const [payout, setPayout] = useState<null | PayoutConfig>(null);
  const [isMonopolyAccountInitialized, setIsMonopolyAccountInitialized] = useState<boolean>(false);
  const [isInitializingAccount, setIsInitializingAccount] = useState(false);
  const [isInitializing, setIsInitializing] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);

  const hasEnoughRoll = Number(userRollBalance) >= 1;

  useEffect(() => {
    (async () => {
      if (!publicKey) return;

      try {
        const [playerStatePda] = derivePlayerStatePda(RTD_PROGRAM_ID, publicKey);
        const accountInfo = await connection.getAccountInfo(playerStatePda);
        if (accountInfo !== null) {
          setIsInitialized(true);
        }
      } catch (err) {
        console.error('Error loading saved keypair:', err);
      }
    })()
  }, [publicKey]);

  const handleInitialize = async () => {
    setIsInitializing(true)
    try {
      const kp = await onInitPlayer();
      if (kp) {
        setRandomnessKp(kp);
        setIsInitialized(true);
        // Save to localStorage
        if (publicKey) {
          localStorage.setItem(
            `rtd-randomness-${publicKey.toString()}`,
            JSON.stringify(Array.from(kp.secretKey))
          );
        }
      }
    } catch (err) {
      console.error('Error initializing:', err);
    }
    setIsInitializing(false)
  };

  const handleInitRandom = async () => {
    setIsSyncing(true)
    try {
      const kp = await onInitRandom();
      if (kp) {
        setRandomnessKp(kp);
        setIsInitialized(true);
        // Save to localStorage
        if (publicKey) {
          localStorage.setItem(
            `rtd-randomness-${publicKey.toString()}`,
            JSON.stringify(Array.from(kp.secretKey))
          );
        }
      }
    } catch (err) {
      console.error('Error initializing:', err);
    }
    setIsSyncing(false)
  };

  async function fetchPlayerState(
    connection: Connection,
    playerStatePubkey: PublicKey
  ): Promise<PlayerState | null> {
    // 1) Fetch account data
    const accountInfo = await connection.getAccountInfo(playerStatePubkey);
    if (!accountInfo) {
      // Account doesn't exist or isn't initialized
      return null;
    }

    const data = accountInfo.data.subarray(8);

    // 3) Deserialize with Borsh
    try {
      const playerState = borsh.deserialize(
        PlayerStateSchema,
        PlayerState,
        data
      );
      return playerState;
    } catch (err) {
      console.error("Error deserializing player state:", err);
      return null;
    }
  }

  const handlePlay = async () => {
    setIsRolling(true);
    try {
      const success = await onPlay();

      if (success) {
        const [playerStatePda] = derivePlayerStatePda(RTD_PROGRAM_ID, publicKey);
        const playerState = await fetchPlayerState(connection, playerStatePda)

        const payout = payoutMap.find(p => p.dice_1 == playerState?.last_dice_1 && p.dice_2 == playerState.last_dice_2)
        console.log('payout, playerState', payout, playerState)
        setPayout(payout || null)
        setCurrentRoll({
          dice1: playerState?.last_dice_1,
          dice2: playerState?.last_dice_2,
        });
      } else {
        // handle error case
        setPayout(null)
        setCurrentRoll({
          dice1: 0,
          dice2: 0,
        });
      }
    } catch (err) {
      console.error('Error playing:', err);
    } finally {
      setIsRolling(false);
      setHasPlayed(true)
    }
  };

  useEffect(() => {
    const checkMonopolyAccount = async () => {
      if (!publicKey) return;

      try {
        const info = await connection.getAccountInfo(userMonopolyTokenAccount);
        setIsMonopolyAccountInitialized(info !== null);
      } catch (err) {
        console.error('Error checking MONOPOLY account:', err);
        setIsMonopolyAccountInitialized(false);
      }
    };

    checkMonopolyAccount();
  }, [publicKey, userMonopolyTokenAccount, connection]);

  const handleInitializeMonopolyAccount = async () => {
    if (!publicKey || !signTransaction) return;

    setIsInitializingAccount(true);
    try {
      const ix = createAssociatedTokenAccountInstruction(
        publicKey, // payer
        userMonopolyTokenAccount, // ata
        publicKey, // owner
        new PublicKey(MONOPOLY_MINT) // mint
      );

      await sendTx(connection, { publicKey, signTransaction }, [ix]);
      setIsMonopolyAccountInitialized(true);
    } catch (err) {
      console.error('Error initializing MONOPOLY account:', err);
      alert('Failed to initialize MONOPOLY account. Please try again.');
    } finally {
      setIsInitializingAccount(false);
    }
  };

  if (isLoadingQueue) {
    return (
      <LoadingIndicator
        message="Setting up..."
        className="min-h-[200px]"
      />
    )
  }

  if (!isMonopolyAccountInitialized) {
    return (
      <TokenAccountInit
        onInitialize={handleInitializeMonopolyAccount}
        isLoading={isInitializingAccount}
      />
    );
  }

  if (!isInitialized) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh]">
        <button
          disabled={disabled}
          onClick={handleInitialize}
          className="group relative px-8 py-4 text-2xl font-bold text-white bg-primary-600 rounded-xl
            hover:bg-primary-700 transform hover:scale-105 transition-all duration-200
            animate-pulse hover:animate-none"
        >
          <span className="flex items-center gap-2">
            {isInitializing ? (
              <Loader2 className="w-5 h-5 animate-spin" />

            ) : (
              <Dice className="w-12 h-12 lg:w-8 lg:h-8" />
            )}
            Initialize Game Account
          </span>
          <div className="absolute -inset-1 bg-primary-400 rounded-xl blur opacity-30 
            group-hover:opacity-50 transition-opacity" />
        </button>
        <p className="mt-4 text-gray-400">
          First time? Create your player account to get started!
        </p>
      </div>
    );
  }

  if (!randomnessKp && !pRandomnessKp) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh]">
        <button
          onClick={handleInitRandom}
          className="group relative px-8 py-4 text-2xl font-bold text-white bg-primary-600 rounded-xl
            hover:bg-primary-700 transform hover:scale-105 transition-all duration-200
            animate-pulse hover:animate-none"
        >
          <span className="flex items-center gap-2">
            {isSyncing ? (
              <Loader2 className="w-5 h-5 animate-spin" />

            ) : (
              <Dice className="w-12 h-12 lg:w-8 lg:h-8" />
            )}
            Sync Account
          </span>
          <div className="absolute -inset-1 bg-primary-400 rounded-xl blur opacity-30 
            group-hover:opacity-50 transition-opacity" />
        </button>
        <p className="mt-4 text-gray-400">
          Sync Your Account To Continue Playing
        </p>
      </div>
    );
  }

  const handleStakeClick = () => {
    window.location.href = '/staking'; // Navigate to staking page
  };

  const handleBuyClick = () => {
    window.location.href = '/swap'; // Navigate to swap page
  };

  return (
    <div className="max-w-4xl mx-auto ">
      <div className="min-h-[40vh] flex flex-col items-center justify-center 
        bg-gray-800 rounded-xl p-8 mb-8">
        <DiceRollAnimation
          isRolling={isRolling}
          dice1={currentRoll.dice1}
          dice2={currentRoll.dice2}
          payout={payout}
          rewardPool={rewardPoolBalance}
          onRoll={handlePlay}
          hasPlayed={hasPlayed}
          transactionStep={transactionStep}
          rollBalance={userRollBalance}
        />
        {hasEnoughRoll || (
          <InsufficientRoll
            onStakeClick={handleStakeClick}
            onBuyClick={handleBuyClick}
          />
        )}
      </div>

      <PayoutTable payoutMap={payoutMap} rewardPoolBalance={rewardPoolBalance} />
    </div>
  );
}
